<div class="org-list-container p-6">
    <jjames-card>
        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
            <div>
                <h1 class="text-2xl font-semibold text-gray-900">Organization Management</h1>
                <p class="text-sm text-gray-600 mt-1">Organizations with a pool parking config on a parking space you
                    can access. Manage their parkers, zones and pool parking configurations.</p>
            </div>
            <div class="flex items-center gap-3">
                <!-- Bulk import is estate-level work, so it follows the same Administrator gate as the
                     route itself rather than being offered to every OperatorAdmin on this page. -->
                @if (isAdministrator()) {
                <jjames-button variant="secondary" iconLeft="fas fa-file-csv" (clicked)="importOrganizations()">
                    Import CSV
                </jjames-button>
                }
                <jjames-button variant="primary" iconLeft="fas fa-plus" (clicked)="createOrganization()">
                    Create Organization
                </jjames-button>
            </div>
        </div>

        <!-- Search -->
        <div class="mb-6 flex flex-wrap items-end gap-4">
            <div class="flex-1 min-w-[220px] max-w-md">
                <label for="search" class="block text-sm font-medium text-gray-700 mb-1">Search by name</label>
                <input type="text" id="search" [ngModel]="searchName()" (ngModelChange)="searchName.set($event)"
                    (keyup.enter)="onSearch()"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="Organization name...">
            </div>
            <!-- Narrows an already-scoped list: the endpoint intersects this with the caller's own
                 grants, so it can only ever remove rows. -->
            @if (accessibleSpaces().length > 0) {
            <div class="min-w-[220px]">
                <label for="spaceFilter" class="block text-sm font-medium text-gray-700 mb-1">Parking space</label>
                <select id="spaceFilter" [value]="selectedSpaceId()"
                    (change)="onSpaceFilterChange($any($event.target).value)"
                    class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm">
                    <option value="">All spaces I can access</option>
                    @for (space of accessibleSpaces(); track space.id) {
                    <option [value]="space.id">{{ space.name }}</option>
                    }
                </select>
            </div>
            }
            <jjames-button variant="secondary" (clicked)="onSearch()">
                Search
            </jjames-button>
        </div>

        <!-- Loading State -->
        @if (loading()) {
        <div class="flex justify-center items-center py-12">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
        }

        <!-- Error Message -->
        @if (successMessage()) {
        <div class="mb-4">
            <jjames-alert type="success" [dismissible]="true" (dismissed)="successMessage.set(null)">
                {{ successMessage() }}
            </jjames-alert>
        </div>
        }

        @if (errorMessage()) {
        <div class="mb-6">
            <jjames-alert type="error" [dismissible]="true" (dismissed)="errorMessage.set(null)">
                {{ errorMessage() }}
            </jjames-alert>
        </div>
        }

        <!-- Organization List -->
        @if (!loading() && organizations().length > 0) {
        <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                    <tr>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Organization Name
                        </th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Description
                        </th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Parkers
                        </th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Zones
                        </th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Pool Configs
                        </th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                        </th>
                    </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                    @for (org of organizations(); track org.id) {
                    <tr class="hover:bg-gray-50 cursor-pointer" (click)="viewOrganization(org.id!)">
                        <td class="px-6 py-4 whitespace-nowrap">
                            <div class="flex items-center gap-2">
                                <span class="text-sm font-medium text-gray-900">{{ org.name }}</span>
                                <!-- Two rows sharing a name are almost always an accidental
                                     double-registration; surface it so an admin can clear one. -->
                                @if (isDuplicate(org)) {
                                <span
                                    class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800"
                                    title="Another organization on this page has the same name">
                                    Duplicate
                                </span>
                                }
                            </div>
                        </td>
                        <td class="px-6 py-4">
                            <div class="text-sm text-gray-500 max-w-xs truncate">{{ org.description || '—' }}</div>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                            <span
                                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {{ getParkerCount(org) }}
                            </span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                            <span
                                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                {{ getZoneCount(org) }}
                            </span>
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap">
                            @if (getPoolConfigCount(org) > 0) {
                            <span
                                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                {{ getPoolConfigCount(org) }}
                            </span>
                            } @else {
                            <span class="text-sm text-gray-400">None</span>
                            }
                        </td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button type="button" (click)="viewOrganization(org.id!); $event.stopPropagation()"
                                class="text-indigo-600 hover:text-indigo-900 inline-flex items-center">
                                <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                View
                            </button>
                            @if (isAdministrator()) {
                            <button type="button" (click)="loginAsOrg(org, $event)"
                                title="Open and view organization portal"
                                class="ml-3 text-emerald-600 hover:text-emerald-800 inline-flex items-center font-medium">
                                <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                                </svg>
                                Log in as Org
                            </button>
                            }
                            <!-- Administrator-only, matching the endpoint's own [Authorize]. Operator
                                 admins reach this list too, and a button that always 403s is worse
                                 than no button. -->
                            @if (isAdministrator()) {
                            <button type="button" (click)="deleteOrganization(org, $event)"
                                [disabled]="deletingId() === org.id"
                                title="Delete organization and its memberships"
                                class="ml-3 text-red-600 hover:text-red-800 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center">
                                <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                {{ deletingId() === org.id ? 'Deleting…' : 'Delete' }}
                            </button>
                            }
                        </td>
                    </tr>
                    }
                </tbody>
            </table>
        </div>

        <!-- Pagination -->
        @if (totalCount() > pageSize()) {
        <div class="flex items-center justify-between px-6 py-3 border-t border-gray-200">
            <div class="text-sm text-gray-700">
                Showing {{ pageIndex() * pageSize() + 1 }} to {{ Math.min((pageIndex() + 1) * pageSize(), totalCount())
                }} of {{ totalCount() }} organizations
            </div>
            <div class="flex gap-2">
                <jjames-button variant="secondary" [disabled]="pageIndex() === 0"
                    (clicked)="onPageChange(pageIndex() - 1)">
                    Previous
                </jjames-button>
                <jjames-button variant="secondary" [disabled]="(pageIndex() + 1) * pageSize() >= totalCount()"
                    (clicked)="onPageChange(pageIndex() + 1)">
                    Next
                </jjames-button>
            </div>
        </div>
        }
        }

        <!-- Empty State -->
        @if (!loading() && organizations().length === 0 && !errorMessage()) {
        <div class="text-center py-12">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">No organizations</h3>
            <p class="mt-1 text-sm text-gray-500">
                @if (selectedSpaceId()) {
                No organization holds a pool parking config on that space.
                } @else {
                No organization holds a pool parking config on a space you can access.
                }
            </p>
            <div class="mt-6">
                <jjames-button variant="primary" iconLeft="fas fa-plus" (clicked)="createOrganization()">
                    Create Organization
                </jjames-button>
            </div>
        </div>
        }
    </jjames-card>
</div>